/**
 * AdminProductPage
 * ------------------------------------------
 * Admin-only page to manage products:
 *  - Create / edit / delete products
 *  - Upload product images to Supabase Storage
 *  - Link products to brands & categories (can create new ones inline)
 *  - Store dimensions & weight for shipping (weight saved in lb)
 *  - Manage product recommendations:
 *      - Recommended row (admin curated) via product_recommendations (type='recommended')
 */

import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import ProductList from "./ProductList";
import {
  AttributesSection,
  BasicInfoSection,
  CategoriesSection,
  GallerySection,
  ProductStatusSection,
  RecommendationNotice,
  RecommendationsSection,
  VariantsSection,
} from "./product/ProductFormSections";
import { ALL_ATTRIBUTES_GROUP_ID } from "./product/constants";
import type {
  Attribute,
  AttributeGroup,
  NamedOption,
  Product,
  ProductVariantDraft,
  RecRow,
} from "./product/types";

const getProductImagePathFromUrl = (url: string) => {
  if (!url) return null;
  const marker = "/product-images/";
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  const path = url.slice(idx + marker.length);
  return path.split("?")[0] || null;
};

const emptyVariant = (sortOrder = 1): ProductVariantDraft => ({
  size_label: "",
  price: "",
  imageFile: null,
  existingImageUrl: "",
  weightOz: "",
  height: "",
  length: "",
  width: "",
  is_active: true,
  sort_order: sortOrder,
});

const AdminProductPage = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAttributeGroupId, setSelectedAttributeGroupId] = useState("");

  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);

  const [variants, setVariants] = useState<ProductVariantDraft[]>([
    emptyVariant(1),
  ]);
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>([]);
  const [galleryImageFiles, setGalleryImageFiles] = useState<File[]>([]);
  const [removedGalleryImageUrls, setRemovedGalleryImageUrls] = useState<string[]>([]);

  const [products, setProducts] = useState<Product[]>([]);
  const [brands, setBrands] = useState<NamedOption[]>([]);
  const [categories, setCategories] = useState<NamedOption[]>([]);

  const [attributeGroups, setAttributeGroups] = useState<AttributeGroup[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<string[]>([]);
  const [attributeSearch, setAttributeSearch] = useState("");

  const [brandId, setBrandId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  const [showBrandInput, setShowBrandInput] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingAdminCheck, setLoadingAdminCheck] = useState(true);

  const [recLoading, setRecLoading] = useState(false);
  const [recSaving, setRecSaving] = useState(false);
  const [recommendedIds, setRecommendedIds] = useState<string[]>([]);
  const [recQuery, setRecQuery] = useState("");

  const galleryFilePreviews = useMemo(
    () =>
      galleryImageFiles.map((file, index) => ({
        file,
        index,
        previewUrl: URL.createObjectURL(file),
      })),
    [galleryImageFiles]
  );

  useEffect(() => {
    return () => {
      galleryFilePreviews.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    };
  }, [galleryFilePreviews]);

  const addVariant = () => {
    setVariants((prev) => [...prev, emptyVariant(prev.length + 1)]);
  };

  const removeVariant = (index: number) => {
    setVariants((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) return [emptyVariant(1)];
      return next.map((v, i) => ({ ...v, sort_order: i + 1 }));
    });
  };

  const addGalleryFiles = (files: File[]) => {
    if (files.length === 0) return;
    setGalleryImageFiles((prev) => [...prev, ...files]);
  };

  const handleGalleryFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.currentTarget.files || []);
    e.currentTarget.value = "";
    addGalleryFiles(selectedFiles);
  };

  const removeGalleryUrl = (url: string) => {
    setGalleryImageUrls((prev) => prev.filter((item) => item !== url));
    setRemovedGalleryImageUrls((prev) => Array.from(new Set([...prev, url])));
  };

  const removeGalleryFile = (index: number) => {
    setGalleryImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVariant = (
    index: number,
    field: keyof ProductVariantDraft,
    value: string | boolean | File | null
  ) => {
    setVariants((prev) =>
      prev.map((variant, i) =>
        i === index ? { ...variant, [field]: value } : variant
      )
    );
  };

  const removeVariantImage = (index: number) => {
    setVariants((prev) =>
      prev.map((variant, i) =>
        i === index
          ? { ...variant, imageFile: null, existingImageUrl: "" }
          : variant
      )
    );
  };

  const toggleAttribute = (attributeId: string) => {
    setSelectedAttributeIds((prev) =>
      prev.includes(attributeId)
        ? prev.filter((id) => id !== attributeId)
        : [...prev, attributeId]
    );
  };

  const setAttributesSelected = (attributeIds: string[], selected: boolean) => {
    setSelectedAttributeIds((prev) => {
      if (selected) {
        return Array.from(new Set([...prev, ...attributeIds]));
      }

      const idsToRemove = new Set(attributeIds);
      return prev.filter((id) => !idsToRemove.has(id));
    });
  };

  const toggleCategory = (id: number) => {
    setCategoryIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((categoryId) => categoryId !== id)
        : [...prev, id];

      setCategoryId((current) => {
        if (current && next.includes(current)) return current;
        return next[0] ?? null;
      });

      return next;
    });
  };

  const uploadProductImage = async (file: File) => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("product-images")
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from("product-images").getPublicUrl(fileName);

    if (!data?.publicUrl) {
      throw new Error("Failed to get public URL for image.");
    }

    return data.publicUrl;
  };

  const deleteUnusedProductImages = async (urls: string[]) => {
    for (const imageUrl of Array.from(new Set(urls))) {
      const path = getProductImagePathFromUrl(imageUrl);
      if (!path) continue;

      const [{ data: productImageRefs }, { data: productGalleryRefs }, { data: variantRefs }] =
        await Promise.all([
          supabase.from("Product").select("id").eq("image_url", imageUrl).limit(1),
          supabase
            .from("Product")
            .select("id")
            .contains("gallery_images", [imageUrl])
            .limit(1),
          supabase.from("ProductVariant").select("id").eq("image_url", imageUrl).limit(1),
        ]);

      const stillUsed =
        (productImageRefs && productImageRefs.length > 0) ||
        (productGalleryRefs && productGalleryRefs.length > 0) ||
        (variantRefs && variantRefs.length > 0);

      if (!stillUsed) {
        const { error } = await supabase.storage.from("product-images").remove([path]);
        if (error) {
          console.error("Failed to delete gallery image from storage:", error);
        }
      }
    }
  };

  const isPositiveNumber = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0;
  };

  const getProductValidationErrors = () => {
    const errors: string[] = [];

    if (!name.trim()) errors.push("Product Name is required.");
    if (!brandId) errors.push("Brand is required.");
    if (categoryIds.length === 0) {
      errors.push("At least one category is required.");
    }

    if (variants.length === 0) {
      errors.push("Add at least one size / variant.");
    }

    variants.forEach((variant, index) => {
      const label = `Size #${index + 1}`;

      if (!variant.size_label.trim()) {
        errors.push(`${label}: Size Label is required.`);
      }
      if (!isPositiveNumber(variant.price)) {
        errors.push(`${label}: Price must be greater than 0.`);
      }
      if (!isPositiveNumber(variant.weightOz)) {
        errors.push(`${label}: Weight must be greater than 0.`);
      }
      if (!isPositiveNumber(variant.height)) {
        errors.push(`${label}: Height must be greater than 0.`);
      }
      if (!isPositiveNumber(variant.length)) {
        errors.push(`${label}: Length must be greater than 0.`);
      }
      if (!isPositiveNumber(variant.width)) {
        errors.push(`${label}: Width must be greater than 0.`);
      }
    });

    return errors;
  };

  const saveStepError = (step: string, error: any) => {
    const details =
      error?.message ||
      error?.details ||
      error?.hint ||
      (typeof error === "string" ? error : "Check console for details.");

    return new Error(`${step}: ${details}`);
  };

  const resetToCreateMode = () => {
    setEditingProductId(null);
    setName("");
    setDescription("");
    setSelectedAttributeGroupId("");
    setAttributeSearch("");
    setBrandId(null);
    setCategoryId(null);
    setCategoryIds([]);
    setIsActive(true);
    setIsFeatured(false);
    setVariants([emptyVariant(1)]);
    setGalleryImageUrls([]);
    setGalleryImageFiles([]);
    setRemovedGalleryImageUrls([]);
    setSelectedAttributeIds([]);
    setShowBrandInput(false);
    setNewBrandName("");
    setShowCategoryInput(false);
    setNewCategoryName("");
    setRecommendedIds([]);
    setRecQuery("");
    setMessage("");
  };

  useEffect(() => {
    checkAdmin();
  }, []);

  useEffect(() => {
    if (isAdmin) fetchInitialData();
  }, [isAdmin]);

  useEffect(() => {
    if (!editingProductId) {
      setRecommendedIds([]);
      setRecQuery("");
      return;
    }
    loadRecommendations(editingProductId);
  }, [editingProductId]);

  const checkAdmin = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setMessage("You are not logged in.");
      setLoadingAdminCheck(false);
      return;
    }

    const { data, error } = await supabase
      .from("Admin")
      .select("*")
      .eq("user_id", user.id);

    if (!error && data && data.length > 0) {
      setIsAdmin(true);
    } else {
      setMessage("⛔ Access denied: Admins only.");
    }

    setLoadingAdminCheck(false);
  };

  const fetchInitialData = async () => {
    const [
      { data: productData },
      { data: brandData },
      { data: categoryData },
      { data: groupData },
      { data: attributeData },
      { data: productCategoryData, error: productCategoryError },
    ] = await Promise.all([
      supabase.from("Product").select("*"),
      supabase.from("Brand").select("*"),
      supabase.from("Category").select("*"),
      supabase.from("attribute_groups").select("*").order("name", { ascending: true }),
      supabase.from("attributes").select("*").order("name", { ascending: true }),
      supabase.from("product_categories").select("product_id, category_id"),
    ]);

    if (productCategoryError) {
      console.warn("Could not load product category links:", productCategoryError);
    }

    const categoriesByProductId = new Map<string, number[]>();
    (productCategoryData || []).forEach((row: any) => {
      const productId = String(row.product_id);
      const linkedCategoryId = Number(row.category_id);
      if (!Number.isFinite(linkedCategoryId)) return;
      categoriesByProductId.set(productId, [
        ...(categoriesByProductId.get(productId) || []),
        linkedCategoryId,
      ]);
    });

    if (productData) {
      setProducts(
        (productData as Product[]).map((product) => {
          const linkedIds = categoriesByProductId.get(product.id) || [];
          const mergedIds = Array.from(new Set([product.categoryId, ...linkedIds]));
          return { ...product, categoryIds: mergedIds };
        })
      );
    }
    if (brandData) setBrands(brandData);
    if (categoryData) setCategories(categoryData);
    if (groupData) setAttributeGroups(groupData as AttributeGroup[]);
    if (attributeData) setAttributes(attributeData as Attribute[]);
  };

  const handleAddNewBrand = async () => {
    if (!newBrandName.trim()) return;

    const { data, error } = await supabase
      .from("Brand")
      .insert([{ name: newBrandName.trim(), logo_url: "" }])
      .select()
      .single();

    if (!error && data) {
      setBrands((prev) => [...prev, data]);
      setBrandId(data.id);
      setNewBrandName("");
      setShowBrandInput(false);
    } else {
      console.error("Brand insert error:", error);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim()) return;

    const { data, error } = await supabase
      .from("Category")
      .insert([{ name: newCategoryName.trim() }])
      .select()
      .single();

    if (!error && data) {
      setCategories((prev) => [...prev, data]);
      setCategoryId(data.id);
      setCategoryIds((prev) => Array.from(new Set([...prev, data.id])));
      setNewCategoryName("");
      setShowCategoryInput(false);
    } else {
      console.error("Category insert error:", error);
    }
  };

  const visibleAttributeGroups = useMemo(() => {
    if (!selectedAttributeGroupId) return [];
    if (selectedAttributeGroupId === ALL_ATTRIBUTES_GROUP_ID) {
      return attributeGroups;
    }
    return attributeGroups.filter((group) => group.id === selectedAttributeGroupId);
  }, [attributeGroups, selectedAttributeGroupId]);

  const attributesByGroupId = useMemo(() => {
    const map = new Map<string, Attribute[]>();

    for (const group of visibleAttributeGroups) {
      map.set(group.id, []);
    }

    for (const attribute of attributes) {
      if (!map.has(attribute.group_id)) continue;
      map.get(attribute.group_id)?.push(attribute);
    }

    return map;
  }, [attributes, visibleAttributeGroups]);

  const selectedAttributeNames = useMemo(() => {
    return attributes
      .filter((attr) => selectedAttributeIds.includes(attr.id))
      .map((attr) => attr.name);
  }, [attributes, selectedAttributeIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    const validationErrors = getProductValidationErrors();
    if (validationErrors.length > 0) {
      setMessage(`Please fix these before saving:\n- ${validationErrors.join("\n- ")}`);
      return;
    }

    try {
      const resolvedVariants = await Promise.all(
        variants.map(async (variant, index) => {
          let imageUrl = variant.existingImageUrl;

          if (variant.imageFile) {
            try {
              imageUrl = await uploadProductImage(variant.imageFile);
            } catch (uploadError) {
              throw saveStepError(
                `Size #${index + 1} image upload failed`,
                uploadError
              );
            }
          }

          const weightInLb = parseFloat(variant.weightOz) / 16;

          return {
            size_label: variant.size_label.trim(),
            price: Number(variant.price),
            image_url: imageUrl || "",
            weight: weightInLb,
            height: parseFloat(variant.height),
            length: parseFloat(variant.length),
            width: parseFloat(variant.width),
            is_active: variant.is_active,
            sort_order: index + 1,
          };
        })
      );

      const defaultVariant =
        resolvedVariants.find((v) => v.is_active) || resolvedVariants[0];
      const uploadedGalleryImages = await Promise.all(
        galleryImageFiles.map(async (file, index) => {
          try {
            return await uploadProductImage(file);
          } catch (uploadError) {
            throw saveStepError(
              `Gallery image #${index + 1} upload failed`,
              uploadError
            );
          }
        })
      );
      const primaryCategoryId =
        categoryId && categoryIds.includes(categoryId) ? categoryId : categoryIds[0];
      const normalizedCategoryIds = Array.from(
        new Set([primaryCategoryId, ...categoryIds].filter(Boolean))
      );
      const resolvedGalleryImages = [...galleryImageUrls, ...uploadedGalleryImages];
      const productImageUrl = defaultVariant.image_url || resolvedGalleryImages[0] || "";

      const productPayload: any = {
        name,
        description,
        brandId,
        categoryId: primaryCategoryId,
        price: defaultVariant.price,
        image_url: productImageUrl,
        height: defaultVariant.height,
        weight: defaultVariant.weight,
        length: defaultVariant.length,
        width: defaultVariant.width,
        gallery_images: resolvedGalleryImages,
        is_active: isActive,
        is_featured: isFeatured,
      };

      let productId = editingProductId;

      if (editingProductId) {
        const { error } = await supabase
          .from("Product")
          .update(productPayload)
          .eq("id", editingProductId);

        if (error) throw saveStepError("Product details could not be updated", error);
      } else {
        const { data, error } = await supabase
          .from("Product")
          .insert([productPayload])
          .select()
          .single();

        if (error) throw saveStepError("Product details could not be saved", error);
        productId = data.id;
        setEditingProductId(data.id);
      }

      if (!productId) throw new Error("Missing product id.");

      const { error: deleteProductCategoriesError } = await supabase
        .from("product_categories")
        .delete()
        .eq("product_id", productId);

      if (deleteProductCategoriesError) {
        throw saveStepError(
          "Existing product categories could not be cleared",
          deleteProductCategoriesError
        );
      }

      const productCategoryRows = normalizedCategoryIds.map((linkedCategoryId) => ({
        product_id: productId,
        category_id: linkedCategoryId,
      }));

      const { error: insertProductCategoriesError } = await supabase
        .from("product_categories")
        .insert(productCategoryRows);

      if (insertProductCategoriesError) {
        throw saveStepError(
          "Selected categories could not be saved",
          insertProductCategoriesError
        );
      }

      if (editingProductId) {
        const { error: deleteOldVariantsError } = await supabase
          .from("ProductVariant")
          .delete()
          .eq("product_id", productId);

        if (deleteOldVariantsError) {
          throw saveStepError("Old size rows could not be replaced", deleteOldVariantsError);
        }
      }

      const variantRows = resolvedVariants.map((variant) => ({
        product_id: productId,
        ...variant,
      }));

      const { error: variantInsertError } = await supabase
        .from("ProductVariant")
        .insert(variantRows);

      if (variantInsertError) {
        throw saveStepError("Size / variant rows could not be saved", variantInsertError);
      }

      const { error: deleteProductAttributesError } = await supabase
        .from("product_attributes")
        .delete()
        .eq("product_id", productId);

      if (deleteProductAttributesError) {
        throw saveStepError(
          "Existing product attributes could not be cleared",
          deleteProductAttributesError
        );
      }

      if (selectedAttributeIds.length > 0) {
        const productAttributeRows = selectedAttributeIds.map((attributeId) => ({
          product_id: productId,
          attribute_id: attributeId,
        }));

        const { error: insertProductAttributesError } = await supabase
          .from("product_attributes")
          .insert(productAttributeRows);

        if (insertProductAttributesError) {
          throw saveStepError(
            "Selected attributes could not be saved",
            insertProductAttributesError
          );
        }
      }

      setMessage(editingProductId ? "✅ Product updated!" : "✅ Product created!");

      setName("");
      setDescription("");
      setSelectedAttributeGroupId("");
      setAttributeSearch("");
      setBrandId(null);
      setCategoryId(null);
      setCategoryIds([]);
      setIsActive(true);
      setIsFeatured(false);
      setVariants([emptyVariant(1)]);
      setGalleryImageUrls([]);
      setGalleryImageFiles([]);
      setRemovedGalleryImageUrls([]);
      setSelectedAttributeIds([]);

      await deleteUnusedProductImages(removedGalleryImageUrls);

      fetchInitialData();
    } catch (error: any) {
      console.error("❌ Save failed:", error);
      setMessage(`Save failed:\n- ${error?.message || "Check console."}`);
    }
  };

  const handleEdit = async (product: Product) => {
    setEditingProductId(product.id);
    setName(product.name);
    setDescription(product.description);
    setBrandId(product.brandId);
    setCategoryId(product.categoryId);
    setCategoryIds(product.categoryIds?.length ? product.categoryIds : [product.categoryId]);
    setGalleryImageUrls(product.gallery_images || []);
    setGalleryImageFiles([]);
    setRemovedGalleryImageUrls([]);
    setIsActive(product.is_active ?? true);
    setIsFeatured(product.is_featured ?? false);
    setMessage("");

    const [
      { data: variantData, error: variantError },
      { data: productAttributeData, error: productAttributeError },
      { data: productCategoryData, error: productCategoryError },
    ] = await Promise.all([
      supabase
        .from("ProductVariant")
        .select("*")
        .eq("product_id", product.id)
        .order("sort_order", { ascending: true }),
      supabase
        .from("product_attributes")
        .select("attribute_id")
        .eq("product_id", product.id),
      supabase
        .from("product_categories")
        .select("category_id")
        .eq("product_id", product.id),
    ]);

    if (productCategoryError) {
      console.error("❌ Failed to load product categories:", productCategoryError);
      setCategoryIds([product.categoryId]);
    } else {
      const linkedIds = (productCategoryData || []).map((row: any) =>
        Number(row.category_id)
      );
      setCategoryIds(Array.from(new Set([product.categoryId, ...linkedIds])));
    }

    if (productAttributeError) {
      console.error("❌ Failed to load product attributes:", productAttributeError);
      setSelectedAttributeIds([]);
    } else {
      setSelectedAttributeIds(
        (productAttributeData || []).map((row: any) => row.attribute_id)
      );
    }

    if (variantError) {
      console.error("❌ Failed to load variants:", variantError);
      setVariants([
        {
          size_label: "Default",
          price: String(product.price ?? ""),
          imageFile: null,
          existingImageUrl: product.image_url || "",
          weightOz: String((product.weight || 0) * 16),
          height: String(product.height ?? ""),
          length: String(product.length ?? ""),
          width: String(product.width ?? ""),
          is_active: product.is_active ?? true,
          sort_order: 1,
        },
      ]);
      return;
    }

    if (!variantData || variantData.length === 0) {
      setVariants([
        {
          size_label: "Default",
          price: String(product.price ?? ""),
          imageFile: null,
          existingImageUrl: product.image_url || "",
          weightOz: String((product.weight || 0) * 16),
          height: String(product.height ?? ""),
          length: String(product.length ?? ""),
          width: String(product.width ?? ""),
          is_active: product.is_active ?? true,
          sort_order: 1,
        },
      ]);
      return;
    }

    setVariants(
      variantData.map((variant: any, index: number) => ({
        size_label: variant.size_label || "",
        price: String(variant.price ?? ""),
        imageFile: null,
        existingImageUrl: variant.image_url || "",
        weightOz: String((variant.weight || 0) * 16),
        height: String(variant.height ?? ""),
        length: String(variant.length ?? ""),
        width: String(variant.width ?? ""),
        is_active: variant.is_active ?? true,
        sort_order: variant.sort_order ?? index + 1,
      }))
    );
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this product?")) return;

    try {
      const { data: prod, error: prodErr } = await supabase
        .from("Product")
        .select("id, image_url")
        .eq("id", id)
        .single();

      if (prodErr) throw prodErr;

      const { data: variantRows, error: variantErr } = await supabase
        .from("ProductVariant")
        .select("image_url")
        .eq("product_id", id);

      if (variantErr) throw variantErr;

      const imageUrls = new Set<string>();
      if (prod?.image_url) imageUrls.add(prod.image_url);
      (variantRows || []).forEach((row: any) => {
        if (row.image_url) imageUrls.add(row.image_url);
      });

      const delRecs = await supabase
        .from("product_recommendations")
        .delete()
        .or(`product_id.eq.${id},recommended_product_id.eq.${id}`);

      if (delRecs.error) throw delRecs.error;

      const { error: delProdErr } = await supabase
        .from("Product")
        .delete()
        .eq("id", id);

      if (delProdErr) throw delProdErr;

      for (const imageUrl of Array.from(imageUrls)) {
        const path = getProductImagePathFromUrl(imageUrl);
        if (!path) continue;

        const { data: otherProducts } = await supabase
          .from("Product")
          .select("id")
          .eq("image_url", imageUrl)
          .limit(1);

        const { data: otherVariants } = await supabase
          .from("ProductVariant")
          .select("id")
          .eq("image_url", imageUrl)
          .limit(1);

        const stillUsed =
          (otherProducts && otherProducts.length > 0) ||
          (otherVariants && otherVariants.length > 0);

        if (!stillUsed) {
          await supabase.storage.from("product-images").remove([path]);
        }
      }

      setProducts((prev) => prev.filter((p) => p.id !== id));
      if (editingProductId === id) resetToCreateMode();
      setMessage("✅ Product deleted.");
    } catch (err: any) {
      console.error("❌ Delete failed:", err?.message || err);
      setMessage("❌ Delete failed. Check console.");
    }
  };

  const loadRecommendations = async (productId: string) => {
    setRecLoading(true);
    try {
      const { data, error } = await supabase
        .from("product_recommendations")
        .select("type, rank, recommended_product_id")
        .eq("product_id", productId)
        .eq("is_active", true)
        .eq("type", "recommended")
        .order("rank", { ascending: true });

      if (error) {
        console.error(error);
        setRecommendedIds([]);
        return;
      }

      const rows = (data || []) as RecRow[];
      setRecommendedIds(rows.map((r) => r.recommended_product_id));
    } finally {
      setRecLoading(false);
    }
  };

  const saveRecommendations = async () => {
    if (!editingProductId) return;

    setRecSaving(true);
    try {
      const del = await supabase
        .from("product_recommendations")
        .delete()
        .eq("product_id", editingProductId)
        .eq("type", "recommended");

      if (del.error) {
        console.error(del.error);
        alert("Failed to remove old recommendations.");
        return;
      }

      const rows: any[] = recommendedIds.map((id, idx) => ({
        product_id: editingProductId,
        recommended_product_id: id,
        type: "recommended",
        rank: idx + 1,
        is_active: true,
      }));

      if (rows.length > 0) {
        const ins = await supabase.from("product_recommendations").insert(rows);
        if (ins.error) {
          console.error(ins.error);
          alert("Failed to save recommendations.");
          return;
        }
      }

      alert("Saved recommendations ✅");
    } finally {
      setRecSaving(false);
    }
  };

  const addRecommended = (id: string) => {
    if (!editingProductId) return;
    if (id === editingProductId) return;
    if (recommendedIds.includes(id)) return;
    setRecommendedIds((prev) => [...prev, id]);
  };

  const removeRecommended = (id: string) => {
    setRecommendedIds((prev) => prev.filter((x) => x !== id));
  };

  const moveRecommended = (id: string, dir: -1 | 1) => {
    const arr = [...recommendedIds];
    const i = arr.indexOf(id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setRecommendedIds(arr);
  };

  const filteredProductsForRec = useMemo(() => {
    const q = recQuery.trim().toLowerCase();
    const base = products.filter((p) => p.id !== editingProductId);
    if (!q) return base.slice(0, 30);
    return base
      .filter((p) => (p.name || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [products, recQuery, editingProductId]);

  const idToProduct = useMemo(() => {
    const map = new Map<string, Product>();
    for (const p of products) map.set(p.id, p);
    return map;
  }, [products]);

  if (loadingAdminCheck) {
    return <div className="p-6 text-center">Checking admin access...</div>;
  }

  if (!isAdmin) {
    return <div className="p-6 text-center text-red-600">{message}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 md:px-6 bg-[#f8f6f4] min-h-screen">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#5c2f2f]">
            {editingProductId ? "Edit Product" : "Add New Product"}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Keep product setup simple and clear for quick client use.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {editingProductId && (
            <button
              type="button"
              onClick={resetToCreateMode}
              className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className="mb-5 whitespace-pre-line rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#5c2f2f] shadow-sm">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <BasicInfoSection
          brands={brands}
          brandId={brandId}
          setBrandId={setBrandId}
          showBrandInput={showBrandInput}
          setShowBrandInput={setShowBrandInput}
          newBrandName={newBrandName}
          setNewBrandName={setNewBrandName}
          handleAddNewBrand={handleAddNewBrand}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
        />

        <CategoriesSection
          categories={categories}
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          categoryIds={categoryIds}
          setCategoryIds={setCategoryIds}
          showCategoryInput={showCategoryInput}
          setShowCategoryInput={setShowCategoryInput}
          newCategoryName={newCategoryName}
          setNewCategoryName={setNewCategoryName}
          handleAddNewCategory={handleAddNewCategory}
          toggleCategory={toggleCategory}
        />

        <AttributesSection
          selectedAttributeGroupId={selectedAttributeGroupId}
          setSelectedAttributeGroupId={setSelectedAttributeGroupId}
          attributeSearch={attributeSearch}
          setAttributeSearch={setAttributeSearch}
          attributeGroups={attributeGroups}
          attributes={attributes}
          selectedAttributeIds={selectedAttributeIds}
          selectedAttributeNames={selectedAttributeNames}
          visibleAttributeGroups={visibleAttributeGroups}
          attributesByGroupId={attributesByGroupId}
          toggleAttribute={toggleAttribute}
          setAttributesSelected={setAttributesSelected}
        />

        <GallerySection
          galleryImageUrls={galleryImageUrls}
          galleryImageFiles={galleryImageFiles}
          galleryFilePreviews={galleryFilePreviews}
          handleGalleryFileChange={handleGalleryFileChange}
          removeGalleryUrl={removeGalleryUrl}
          removeGalleryFile={removeGalleryFile}
        />

        <VariantsSection
          variants={variants}
          addVariant={addVariant}
          removeVariant={removeVariant}
          updateVariant={updateVariant}
          removeVariantImage={removeVariantImage}
        />

        <ProductStatusSection
          isActive={isActive}
          setIsActive={setIsActive}
          isFeatured={isFeatured}
          setIsFeatured={setIsFeatured}
        />

        <div className="sticky bottom-4 z-10">
          <div className="flex flex-wrap gap-3 justify-end rounded-2xl border border-gray-200 bg-white/95 backdrop-blur px-4 py-4 shadow-lg">
            {editingProductId && (
              <button
                type="button"
                onClick={resetToCreateMode}
                className="px-5 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              className="px-6 py-3 rounded-xl bg-[#5c2f2f] text-white font-semibold hover:bg-[#7a4444]"
            >
              {editingProductId ? "Update Product" : "Create Product"}
            </button>
          </div>
        </div>
      </form>

      {!editingProductId && <RecommendationNotice />}

      {editingProductId && (
        <RecommendationsSection
          recLoading={recLoading}
          recSaving={recSaving}
          recQuery={recQuery}
          setRecQuery={setRecQuery}
          filteredProductsForRec={filteredProductsForRec}
          recommendedIds={recommendedIds}
          idToProduct={idToProduct}
          addRecommended={addRecommended}
          removeRecommended={removeRecommended}
          moveRecommended={moveRecommended}
          saveRecommendations={saveRecommendations}
        />
      )}

      <div className="mt-8">
        <ProductList
          products={products}
          onDelete={handleDelete}
          onEdit={handleEdit}
          brands={brands}
          categories={categories}
        />
      </div>
    </div>
  );
};

export default AdminProductPage;
